import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, FileText, CheckCircle, XCircle, Loader2, Search, ArrowRight, ArrowLeft, Banknote, Link2, Check, ChevronDown, Users, History, Eye, CheckCheck, Sparkles, Ban, Layers, TrendingUp, TrendingDown, BarChart3, Plus, Trash2, RefreshCw, CalendarRange, Wallet, X, AlertCircle } from "lucide-react";
import { bankStatementAPI, clientAPI, adminAPI, supplierInvoiceAPI, compteCourantAssocieAPI } from "@/lib/api";
import MatchStep from "@/components/MatchStep";
import { toast } from "sonner";
import { DateField } from "@/components/ui/date-field";

// Sources of financial movements (bank, PayPal, etc.).
// `activeClass` is the Tailwind classes when the source pill is selected.
// `badgeClass` is used on Historique rows for the read-only badge.
const SOURCE_META = {
  banque: {
    label: "Banque",
    activeClass: "bg-indigo-100 text-indigo-700 border-indigo-300",
    badgeClass: "bg-indigo-50 text-indigo-700 border-indigo-200",
  },
  paypal: {
    label: "PayPal",
    activeClass: "bg-blue-100 text-blue-700 border-blue-300",
    badgeClass: "bg-blue-50 text-blue-700 border-blue-200",
  },
};

function formatEur(v) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);
}

// Poll an async bank parse/extract/reextract job until it's done or errored.
// onTick(label) is called every poll so the UI can show progress text.
// Resilient: tolerates transient 524/network errors (the job keeps running
// server-side, so we keep polling for up to maxAttempts).
async function pollBankParseJob(jobId, onTick) {
  const maxAttempts = 240; // 240 * 2s = 8 min hard cap
  let consecutiveErrors = 0;
  const maxConsecutiveErrors = 5; // ~10s of network instability tolerated
  for (let i = 0; i < maxAttempts; i++) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise(r => setTimeout(r, 2000));
    try {
      // eslint-disable-next-line no-await-in-loop
      const { data } = await bankStatementAPI.parseStatus(jobId);
      consecutiveErrors = 0;
      if (onTick && data.step_label) onTick(data.step_label);
      if (data.status === "done") return data.result;
      if (data.status === "error") throw new Error(data.error || "Erreur extraction");
    } catch (e) {
      // Server-side job is independent of polling — a transient network/proxy
      // error doesn't mean the job failed. Retry a few times before giving up.
      const status = e.response?.status;
      const isTransient = !status || status === 502 || status === 503 || status === 504 || status === 524;
      if (!isTransient) throw e;
      consecutiveErrors += 1;
      if (consecutiveErrors >= maxConsecutiveErrors) throw new Error("Connexion instable, réessayez (le serveur ne répond plus)");
      if (onTick) onTick(`Connexion lente, nouvel essai (${consecutiveErrors}/${maxConsecutiveErrors})…`);
    }
  }
  throw new Error("Délai d'extraction dépassé (8 minutes). Réessayez.");
}

const PAYMENT_MODES = {
  virement: "Virement", cheque: "Chèque", carte: "Carte",
  especes: "Espèces", prélèvement: "Prélèvement", autre: "Autre"
};

// ====== Step 1: Upload ======
function UploadStep({ onParsed }) {
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState("");
  const [source, setSource] = useState("banque");
  const [duplicate, setDuplicate] = useState(null); // { existing, file, filename }

  const processFile = async (file, force = false) => {
    setLoading(true);
    setProgress("");
    try {
      const res = await bankStatementAPI.parse(file, force);
      let data = res.data;
      if (data.duplicate) {
        setDuplicate({ existing: data.existing, file, filename: file.name });
        setLoading(false);
        setProgress("");
        return;
      }
      const fileHash = data.file_hash;
      const storagePath = data.storage_path;
      if (data.is_async && data.job_id) {
        setProgress("Extraction IA en arrière-plan…");
        toast.info("Extraction IA en cours, cela peut prendre 1 à 3 min sur un gros PDF…", { duration: 6000 });
        data = await pollBankParseJob(data.job_id, (lbl) => setProgress(lbl));
      }
      if ((data.count || 0) === 0 && (data.debit_count || 0) === 0) { toast.error("Aucune opération trouvée"); return; }
      const msgs = [];
      if (data.count > 0) msgs.push(`${data.count} crédit(s)`);
      if (data.debit_count > 0) msgs.push(`${data.debit_count} débit(s)`);
      toast.success(`${msgs.join(" et ")} extrait(s)`);
      onParsed(data.transactions, file.name, data.debits || [], source, fileHash, storagePath || data.storage_path);
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Erreur lecture"); }
    finally { setLoading(false); setProgress(""); }
  };

  const handleFile = (file) => {
    if (!file) return;
    processFile(file, false);
  };

  const handleForceImport = () => {
    if (!duplicate?.file) return;
    const f = duplicate.file;
    setDuplicate(null);
    processFile(f, true);
  };

  const sourceMeta = SOURCE_META[source] || SOURCE_META.banque;

  return (
    <>
    <Card className="p-8 border-2 border-dashed border-slate-300 text-center"
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
      data-testid="upload-area"
    >
      <div className={`py-8 transition-colors rounded-lg ${dragging ? "bg-indigo-50" : ""}`}>
        <Upload size={40} className="mx-auto text-slate-400 mb-4" />
        <p className="text-base font-medium text-slate-700 mb-1">Déposer votre relevé {sourceMeta.label.toLowerCase()}</p>
        <p className="text-xs text-slate-400 mb-4">CSV ou PDF — tous les mouvements (crédits et débits) seront extraits automatiquement et affichés dans les onglets correspondants</p>
        {/* Source selector */}
        <div className="flex items-center justify-center gap-2 mb-4">
          <span className="text-[10px] text-slate-500 uppercase">Source :</span>
          {Object.entries(SOURCE_META).map(([key, meta]) => (
            <button key={key} onClick={() => setSource(key)}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-medium border transition-colors ${source === key ? meta.activeClass : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}
              data-testid={`source-${key}`}>
              {meta.label}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="flex flex-col items-center justify-center gap-2 text-indigo-600">
            <div className="flex items-center gap-2"><Loader2 size={16} className="animate-spin" /><span className="text-xs">{progress || "Analyse en cours..."}</span></div>
            <span className="text-[10px] text-slate-400">L’extraction tourne en arrière-plan, ne fermez pas l’onglet.</span>
          </div>
        ) : (
          <label className="inline-block">
            <input type="file" accept=".csv,.txt,.pdf" className="hidden" onChange={e => handleFile(e.target.files[0])} data-testid="file-input" />
            <span className="inline-flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg cursor-pointer text-xs font-medium transition-colors">
              <FileText size={14} /> Choisir un fichier
            </span>
          </label>
        )}
      </div>
    </Card>
    {duplicate && (
      <DuplicateDialog
        duplicate={duplicate}
        onClose={() => setDuplicate(null)}
        onForce={handleForceImport}
      />
    )}
    </>
  );
}


function DuplicateDialog({ duplicate, onClose, onForce }) {
  const { existing, filename } = duplicate;
  const isDraft = existing.kind === "draft";
  const dateStr = (() => {
    try { return new Date(existing.created_at).toLocaleString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris" }); }
    catch { return existing.created_at || "—"; }
  })();
  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="duplicate-dialog">
      <Card className="max-w-md w-full p-6 border border-amber-200">
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 rounded-full bg-amber-50">
            <AlertCircle size={20} className="text-amber-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Ce relevé semble déjà importé
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Un fichier identique <span className="font-medium">({existing.filename || filename})</span>{" "}
              {isDraft ? "est déjà ouvert en brouillon" : "a déjà été traité"} le{" "}
              <span className="font-medium">{dateStr}</span>
              {!isDraft && existing.payments_created > 0 && (
                <> avec <span className="font-medium">{existing.payments_created} règlement(s)</span> créé(s)</>
              )}
              .
            </p>
            <div className="mt-3 text-[11px] text-slate-500 bg-slate-50 rounded px-2 py-1.5 border border-slate-200">
              {existing.total_lines} ligne(s) · {(existing.total_amount || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button variant="outline" onClick={onClose} className="text-xs" data-testid="duplicate-cancel">
            Annuler — ne pas importer
          </Button>
          <Button onClick={onForce} className="bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid="duplicate-force">
            Importer quand même
          </Button>
        </div>
      </Card>
    </div>
  );
}


// ====== Step 3: Done ======
function DoneStep({ result, onReset }) {
  return (
    <Card className="p-8 text-center">
      <CheckCircle size={40} className="mx-auto text-emerald-500 mb-3" />
      <h2 className="text-lg font-bold text-slate-800 mb-1">Règlements enregistrés</h2>
      <p className="text-sm text-slate-500 mb-4">{result.created} règlement(s) créé(s) avec succès</p>
      {result.errors > 0 && <p className="text-xs text-red-500 mb-3">{result.errors} erreur(s)</p>}
      <Button onClick={onReset} variant="outline" size="sm" data-testid="reset-btn">Importer un autre relevé</Button>
    </Card>
  );
}

// ====== P0: History ======
function HistoryView({ onResumeDraft }) {
  const [statements, setStatements] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteDraft, setConfirmDeleteDraft] = useState(null);
  const [resumingId, setResumingId] = useState(null);

  const handleResumeStatement = async (s) => {
    setResumingId(s.id);
    try {
      const res = await bankStatementAPI.resumeStatementAsDraft(s.id);
      toast.success(`Reprise du relevé ${s.filename || "—"}`);
      if (onResumeDraft) onResumeDraft(res.data.draft_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la reprise");
    } finally {
      setResumingId(null);
    }
  };

  const reload = () => {
    setLoading(true);
    Promise.all([
      bankStatementAPI.list().catch(() => ({ data: [] })),
      bankStatementAPI.listDrafts().catch(() => ({ data: [] })),
    ])
      .then(([s, d]) => { setStatements(s.data || []); setDrafts(d.data || []); })
      .catch(() => toast.error("Erreur chargement historique"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, []);

  const viewDetail = async (id) => {
    setDetailLoading(true);
    try {
      const res = await bankStatementAPI.get(id);
      setDetail(res.data);
    } catch { toast.error("Erreur chargement"); }
    finally { setDetailLoading(false); }
  };

  // Stream the original PDF from object storage and open it in a new tab.
  // We fetch with auth (cookie) rather than building a URL the browser can hit
  // directly, so the file stays gated by tenant auth.
  const openPdf = async (id) => {
    try {
      const res = await bankStatementAPI.downloadPdf(id);
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      // Response is a Blob when responseType is "blob" — read the JSON detail manually.
      let msg = "PDF introuvable";
      const errBlob = e.response?.data;
      if (errBlob && errBlob.text) {
        try { const j = JSON.parse(await errBlob.text()); msg = j.detail || msg; } catch { /* keep default */ }
      }
      toast.error(msg);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      const res = await bankStatementAPI.deleteStatement(confirmDelete.id);
      toast.success(`Relevé "${res.data.filename || confirmDelete.id}" supprimé`);
      setConfirmDelete(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur suppression");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteDraft = async () => {
    if (!confirmDeleteDraft) return;
    setDeleting(true);
    try {
      await bankStatementAPI.deleteDraft(confirmDeleteDraft.id);
      toast.success(`Brouillon "${confirmDeleteDraft.filename || confirmDeleteDraft.id}" supprimé`);
      setConfirmDeleteDraft(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur suppression");
    } finally {
      setDeleting(false);
    }
  };

  const [reextractingId, setReextractingId] = useState(null);
  const handleReextract = async (s) => {
    if (!s.has_pdf) {
      toast.error("Ce relevé a été importé avant le stockage du PDF. Re-uploadez le fichier original via 'Charger' une seule fois — ensuite la re-extraction sera automatique.", { duration: 10000 });
      return;
    }
    setReextractingId(s.id);
    try {
      const res = await bankStatementAPI.reextract(s.id);
      let data = res.data;
      if (data.is_async && data.job_id) {
        toast.info("Re-extraction IA en cours en arrière-plan…", { duration: 5000 });
        data = await pollBankParseJob(data.job_id);
      }
      toast.success(`${s.filename}: ${data.message}`);
      if (data.extraction_warning) {
        toast.warning(`${s.filename} — ${data.extraction_warning}`, {
          duration: 12000,
          description: `Extrait: ${data.sum_extracted}€ · Déclaré PDF: ${data.total_debits_declared}€`,
        });
      }
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Erreur re-extraction");
    } finally {
      setReextractingId(null);
    }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="animate-spin h-5 w-5 text-slate-400" /></div>;

  if (detail) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setDetail(null)}>
            <ArrowLeft size={13} className="mr-1" /> Retour
          </Button>
          <h3 className="text-sm font-semibold text-slate-800">{detail.filename || "Relevé"}</h3>
          <Badge variant="outline" className="text-[10px]">{new Date(detail.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</Badge>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-3"><p className="text-[10px] text-slate-500 uppercase">Lignes</p><p className="text-lg font-bold text-slate-900">{detail.total_lines}</p></Card>
          <Card className="p-3"><p className="text-[10px] text-slate-500 uppercase">Montant total</p><p className="text-lg font-bold text-slate-900">{formatEur(detail.total_amount)}</p></Card>
          <Card className="p-3"><p className="text-[10px] text-emerald-500 uppercase">Règlements créés</p><p className="text-lg font-bold text-emerald-600">{detail.payments_created}</p></Card>
          <Card className="p-3"><p className="text-[10px] text-red-500 uppercase">Erreurs</p><p className="text-lg font-bold text-red-600">{detail.errors}</p></Card>
        </div>

        {detail.payments_details && detail.payments_details.length > 0 && (
          <Card className="border border-slate-200 rounded-lg bg-white overflow-hidden">
            <div className="p-3 border-b border-slate-100">
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Règlements créés</p>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-[10px] uppercase text-slate-500 px-3">Facture</TableHead>
                  <TableHead className="text-[10px] uppercase text-slate-500 px-3">Client</TableHead>
                  <TableHead className="text-[10px] uppercase text-slate-500 px-3">Date</TableHead>
                  <TableHead className="text-[10px] uppercase text-slate-500 px-3">Mode</TableHead>
                  <TableHead className="text-[10px] uppercase text-slate-500 px-3 text-right">Montant</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detail.payments_details.map((p, i) => (
                  <TableRow key={p.document_number || `payment-${i}`}>
                    <TableCell className="text-[11px] px-3 font-mono text-indigo-600">{p.document_number}</TableCell>
                    <TableCell className="text-[11px] px-3">{p.client_name || "-"}</TableCell>
                    <TableCell className="text-[11px] px-3">{p.date}</TableCell>
                    <TableCell className="text-[11px] px-3 capitalize">{PAYMENT_MODES[p.mode] || p.mode}</TableCell>
                    <TableCell className="text-[11px] px-3 text-right font-medium">{formatEur(p.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {drafts.length === 0 && statements.length === 0 ? (
        <Card className="p-8 text-center">
          <History size={32} className="mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">Aucun relevé traité</p>
          <p className="text-xs text-slate-400 mt-1">Les relevés importés apparaîtront ici</p>
        </Card>
      ) : (
        <Card className="border border-slate-200 rounded-lg bg-white overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-[10px] uppercase text-slate-500 px-3 w-24">Statut</TableHead>
                <TableHead className="text-[10px] uppercase text-slate-500 px-3">Date</TableHead>
                <TableHead className="text-[10px] uppercase text-slate-500 px-3">Fichier</TableHead>
                <TableHead className="text-[10px] uppercase text-slate-500 px-3 text-right">Lignes</TableHead>
                <TableHead className="text-[10px] uppercase text-slate-500 px-3 text-right">Montant</TableHead>
                <TableHead className="text-[10px] uppercase text-slate-500 px-3 text-right">Règlements</TableHead>
                <TableHead className="text-[10px] uppercase text-slate-500 px-3 w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Drafts in progress — orange (some assigned) or red (none assigned) */}
              {drafts.map(d => {
                const assignedCount = d.assigned_count || 0;
                const isStarted = assignedCount > 0;
                const isComplete = d.total_lines > 0 && assignedCount >= d.total_lines;
                let dotColor = "bg-red-500";
                let labelColor = "text-red-700";
                let bgColor = "bg-red-50/30";
                let label = "À commencer";
                if (isComplete) {
                  dotColor = "bg-emerald-500";
                  labelColor = "text-emerald-700";
                  bgColor = "bg-emerald-50/40";
                  label = "Prêt à finaliser";
                } else if (isStarted) {
                  dotColor = "bg-amber-400";
                  labelColor = "text-amber-700";
                  bgColor = "bg-amber-50/40";
                  label = `En cours · ${d.progress}%`;
                }
                return (
                  <TableRow key={d.id} className={`${bgColor} hover:bg-slate-50/50`} data-testid={`draft-row-${d.id}`}>
                    <TableCell className="px-3">
                      <div className="flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full ${dotColor}`} />
                        <span className={`text-[10px] font-medium ${labelColor}`} data-testid={`draft-status-${d.id}`}>{label}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-[11px] px-3 whitespace-nowrap">{new Date(d.created_at || d.updated_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}</TableCell>
                    <TableCell className="text-[11px] px-3 font-medium text-slate-700">
                      <div className="flex items-center gap-1.5">
                        <span>{d.filename || "-"}</span>
                        {d.source && d.source !== "banque" && (
                          <span className={`text-[9px] px-1.5 py-0.5 rounded border ${(SOURCE_META[d.source] || SOURCE_META.banque).badgeClass}`}
                            data-testid={`draft-source-${d.id}`}>
                            {(SOURCE_META[d.source] || SOURCE_META.banque).label}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-[11px] px-3 text-right">{d.total_lines}</TableCell>
                    <TableCell className="text-[11px] px-3 text-right font-medium">{formatEur(d.total_amount)}</TableCell>
                    <TableCell className="text-[11px] px-3 text-right">
                      <Badge variant="outline" className={`text-[10px] ${isComplete ? "text-emerald-600 border-emerald-300" : "text-amber-600 border-amber-300"}`}>
                        {d.assigned_count}/{d.total_lines}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="default" size="sm"
                          className={`h-6 px-2 text-[10px] text-white ${isComplete ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-600 hover:bg-amber-700"}`}
                          onClick={() => onResumeDraft && onResumeDraft(d.id)}
                          title={isComplete ? "Finaliser le brouillon (toutes les lignes sont traitées)" : "Reprendre le rapprochement"}
                          data-testid={`resume-draft-${d.id}`}>
                          {isComplete ? <Check size={11} className="mr-0.5" /> : <ArrowRight size={11} className="mr-0.5" />}
                          {isComplete ? "Finaliser" : "Reprendre"}
                        </Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6 hover:bg-red-50"
                          onClick={() => setConfirmDeleteDraft(d)}
                          title="Supprimer ce brouillon"
                          data-testid={`delete-draft-${d.id}`}>
                          <Trash2 size={13} className="text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {/* Completed statements — green=complet / amber=partiel / red=non rapproché */}
              {statements.map(s => {
                const created = s.payments_created || 0;
                const total = s.total_lines || 0;
                // Phase 2 hotfix BUG A : a statement finalized with every line
                // marked "Déjà traité" / "manually_skipped" has 0 payments but
                // is fully handled — don't show it in red. Flag computed by
                // the backend in GET /bank-statements.
                const allManuallyHandled = !!s.all_lines_manually_handled;
                let dotColor = "bg-emerald-500";
                let labelColor = "text-emerald-700";
                let label = "Complet";
                if (total === 0) {
                  // Statements with 0 lines (manual debits container or empty PDF) — keep neutral
                  dotColor = "bg-slate-400";
                  labelColor = "text-slate-500";
                  label = "Vide";
                } else if (allManuallyHandled) {
                  dotColor = "bg-emerald-500";
                  labelColor = "text-emerald-700";
                  label = `Traité manuellement · ${total}/${total}`;
                } else if (created === 0) {
                  dotColor = "bg-red-500";
                  labelColor = "text-red-700";
                  label = `0/${total} · Non rapproché`;
                } else if (created < total) {
                  dotColor = "bg-amber-400";
                  labelColor = "text-amber-700";
                  label = `Partiel · ${created}/${total}`;
                } else {
                  label = `Complet · ${created}/${total}`;
                }
                return (
                <TableRow key={s.id} className="hover:bg-slate-50/50" data-testid={`history-row-${s.id}`}>
                  <TableCell className="px-3">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${dotColor}`} />
                      <span className={`text-[10px] font-medium ${labelColor}`} data-testid={`statement-status-${s.id}`}>{label}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-[11px] px-3 whitespace-nowrap">{new Date(s.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}</TableCell>
                  <TableCell className="text-[11px] px-3 font-medium text-slate-700">
                    <div className="flex items-center gap-1.5">
                      <span>{s.filename || "-"}</span>
                      {s.source && s.source !== "banque" && (
                        <span className={`text-[9px] px-1.5 py-0.5 rounded border ${(SOURCE_META[s.source] || SOURCE_META.banque).badgeClass}`}
                          data-testid={`statement-source-${s.id}`}>
                          {(SOURCE_META[s.source] || SOURCE_META.banque).label}
                        </span>
                      )}
                      {s.pending_reconciliation && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-300 font-medium"
                          data-testid={`statement-pending-${s.id}`}
                          title="Les débits sont déjà comptés. Termine le rapprochement des crédits pour finaliser ce relevé.">
                          En attente de rapprochement
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-[11px] px-3 text-right">{s.total_lines}</TableCell>
                  <TableCell className="text-[11px] px-3 text-right font-medium">{formatEur(s.total_amount)}</TableCell>
                  <TableCell className="text-[11px] px-3 text-right">
                    <Badge variant="outline" className={`text-[10px] ${s.payments_created > 0 ? "text-emerald-600 border-emerald-300" : "text-slate-400"}`}>{s.payments_created}</Badge>
                  </TableCell>
                  <TableCell className="px-3">
                    <div className="flex items-center justify-end gap-1">
                      {created > 0 && created < total && (
                        <Button variant="default" size="sm" className="h-6 px-2 text-[10px] bg-amber-600 hover:bg-amber-700 text-white"
                          onClick={() => handleResumeStatement(s)}
                          disabled={resumingId === s.id}
                          title="Reprendre le rapprochement des lignes restantes"
                          data-testid={`continue-statement-${s.id}`}>
                          {resumingId === s.id
                            ? <Loader2 size={11} className="animate-spin mr-0.5" />
                            : <ArrowRight size={11} className="mr-0.5" />}
                          Continuer
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => viewDetail(s.id)} data-testid={`view-detail-${s.id}`}>
                        <Eye size={13} className="text-slate-500" />
                      </Button>
                      {s.storage_path && (
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openPdf(s.id)}
                          title="Voir le PDF du relevé"
                          data-testid={`view-pdf-${s.id}`}>
                          <FileText size={13} className="text-indigo-500" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-6 w-6 hover:bg-red-50"
                        onClick={() => setConfirmDelete(s)}
                        title="Supprimer ce relevé"
                        data-testid={`delete-statement-${s.id}`}>
                        <Trash2 size={13} className="text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="delete-statement-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce relevé ?</AlertDialogTitle>
            <AlertDialogDescription>
              Vous êtes sur le point de supprimer définitivement le relevé{" "}
              <span className="font-semibold text-slate-800">{confirmDelete?.filename || confirmDelete?.id}</span>{" "}
              ({confirmDelete?.total_lines || 0} ligne(s), {formatEur(confirmDelete?.total_amount || 0)}).
              Cette action est irréversible. Les rapprochements liés à ce relevé seront aussi nettoyés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-statement">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting}
              className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-statement">
              {deleting ? "Suppression..." : "Supprimer définitivement"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmDeleteDraft} onOpenChange={(v) => { if (!v) setConfirmDeleteDraft(null); }}>
        <AlertDialogContent data-testid="delete-draft-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce brouillon ?</AlertDialogTitle>
            <AlertDialogDescription>
              Vous êtes sur le point de supprimer le brouillon en cours{" "}
              <span className="font-semibold text-slate-800">{confirmDeleteDraft?.filename || confirmDeleteDraft?.id}</span>{" "}
              ({confirmDeleteDraft?.assigned_count || 0}/{confirmDeleteDraft?.total_lines || 0} ligne(s) affectée(s)).
              Les affectations en cours seront perdues — le fichier importé n&apos;est pas affecté et pourra être ré-importé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-draft">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteDraft} disabled={deleting}
              className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-draft">
              {deleting ? "Suppression..." : "Supprimer le brouillon"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ====== P0: Debits View ======
function DebitsView({ setTab }) {
  const [debits, setDebits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [categorizing, setCategorizing] = useState(false);
  const [total, setTotal] = useState(0);
  const [byCategory, setByCategory] = useState({});
  const [categories, setCategories] = useState([]);
  const [filterCat, setFilterCat] = useState("");
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ date: "", label: "", amount: "", category: "" });
  const [manualSaving, setManualSaving] = useState(false);
  const [selectedDebitIds, setSelectedDebitIds] = useState(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [supplierLinkDebit, setSupplierLinkDebit] = useState(null);
  // Fiscal year filtering — mirrors IncomeStatementView so debits and CR
  // use the exact same period boundaries (otherwise the user would see
  // debits "2024" in civil-year while the CR shows "01/10/24→30/09/25").
  const [companyStartMonth, setCompanyStartMonth] = useState(1);
  const now = new Date();
  // For fiscal year starting on month M, "fyStart year Y" = period Y/M/1 → Y+1/M-1/last.
  // If M == 1 it behaves exactly like a civil year.
  const computeDefaultFyStart = (startMonth) => {
    const m = startMonth || 1;
    return (now.getMonth() + 1) >= m ? now.getFullYear() : now.getFullYear() - 1;
  };
  const [fyStart, setFyStart] = useState(computeDefaultFyStart(1));
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [fullYear, setFullYear] = useState(true);
  // Les mois suivent l'EXERCICE, pas le calendrier : sur un exercice ouvert en
  // octobre, le sélecteur commence à Oct et se termine en Sept. Chaque bouton
  // porte l'année civile du mois, sinon « Janv » serait ambigu sur 2 ans.
  const MONTH_LABELS = ["Janv", "Fév", "Mars", "Avr", "Mai", "Juin", "Juil", "Août", "Sept", "Oct", "Nov", "Déc"];
  const fiscalMonths = () => {
    const start = companyStartMonth || 1;
    return Array.from({ length: 12 }, (_, i) => {
      const value = ((start - 1 + i) % 12) + 1;
      const calYear = value >= start ? fyStart : fyStart + 1;
      return {
        value,
        label: start === 1 ? MONTH_LABELS[value - 1] : `${MONTH_LABELS[value - 1]} ${String(calYear).slice(-2)}`,
      };
    });
  };

  useEffect(() => {
    bankStatementAPI.debitCategories().then(r => setCategories(r.data || [])).catch(() => {});
    // Fetch fiscal year config — reuse the same setting as the CR view.
    adminAPI.getSettings().then(r => {
      const sm = (r.data && r.data.fiscal_year_start_month) || 1;
      setCompanyStartMonth(sm);
      setFyStart(computeDefaultFyStart(sm));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helpers shared with IncomeStatementView (kept inline to avoid yet another util file)
  const fyRange = (startYear, startMonth) => {
    const m = String(startMonth).padStart(2, "0");
    const endYear = startMonth === 1 ? startYear : startYear + 1;
    const endMonth = startMonth === 1 ? 12 : startMonth - 1;
    const lastDay = new Date(endYear, endMonth, 0).getDate();
    return { from: `${startYear}-${m}-01`, to: `${endYear}-${String(endMonth).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}` };
  };
  const fyLabel = (startYear, startMonth) => startMonth === 1 ? String(startYear) : `${startYear}-${startYear + 1}`;
  const fyRangeText = (startYear, startMonth) => {
    if (startMonth === 1) return `01/01/${startYear} → 31/12/${startYear}`;
    const { from, to } = fyRange(startYear, startMonth);
    const fmt = (s) => { const [y, m, d] = s.split("-"); return `${d}/${m}/${y}`; };
    return `${fmt(from)} → ${fmt(to)}`;
  };

  const loadDebits = useCallback(() => {
    setLoading(true);
    const params = {};
    const { from, to } = fyRange(fyStart, companyStartMonth);
    if (fullYear) {
      params.date_from = from;
      params.date_to = to;
    } else if (selectedMonth) {
      const m = String(selectedMonth).padStart(2, "0");
      // Find the calendar year that holds this month within the fiscal year
      const calYear = selectedMonth >= companyStartMonth ? fyStart : fyStart + 1;
      const lastDay = new Date(calYear, selectedMonth, 0).getDate();
      params.date_from = `${calYear}-${m}-01`;
      params.date_to = `${calYear}-${m}-${String(lastDay).padStart(2, "0")}`;
    }
    bankStatementAPI.debits(params)
      .then(r => { setDebits(r.data.debits || []); setTotal(r.data.total || 0); setByCategory(r.data.by_category || {}); })
      .catch(() => toast.error("Erreur chargement débits"))
      .finally(() => setLoading(false));
  }, [fyStart, companyStartMonth, selectedMonth, fullYear]);

  useEffect(() => { loadDebits(); setSelectedDebitIds(new Set()); }, [loadDebits]);

  const handleExtractDebits = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    let totalAdded = 0;
    for (const file of files) {
      try {
        const res = await bankStatementAPI.extractDebits(file);
        let data = res.data;
        if (data.is_async && data.job_id) {
          toast.info(`${file.name}: extraction IA en cours…`, { duration: 5000 });
          // eslint-disable-next-line no-await-in-loop
          data = await pollBankParseJob(data.job_id);
        }
        toast.success(`${file.name}: ${data.message}`);
        totalAdded += data.added || 0;
        // Surface the AI-extraction warning to the user (e.g. when the AI missed
        // a line and the declared total in the PDF doesn't match the extracted sum).
        if (data.extraction_warning) {
          toast.warning(`${file.name} — ${data.extraction_warning}`, {
            duration: 12000,
            description: `Extrait: ${data.sum_extracted}€ · Déclaré PDF: ${data.total_debits_declared}€`,
          });
        }
      } catch (err) {
        toast.error(`${file.name}: ${err.response?.data?.detail || err.message || "Erreur"}`);
      }
    }
    e.target.value = "";
    setUploading(false);
    if (totalAdded > 0) loadDebits();
  };

  const handleCategorize = async () => {
    setCategorizing(true);
    try {
      const res = await bankStatementAPI.categorizeDebits();
      if (res.data.status === "done") {
        toast.success(res.data.message || "Tous les débits sont déjà catégorisés");
        setCategorizing(false);
        loadDebits();
        return;
      }
      toast.info(res.data.message || "Catégorisation lancée en arrière-plan...");
      // Poll for completion
      const poll = setInterval(async () => {
        try {
          const status = await bankStatementAPI.categorizeStatus();
          if (status.data.status === "done") {
            clearInterval(poll);
            setCategorizing(false);
            toast.success(`${status.data.done || 0} débit(s) catégorisé(s)`);
            loadDebits();
          }
        } catch {
          clearInterval(poll);
          setCategorizing(false);
        }
      }, 3000);
      // Safety timeout after 2 minutes
      setTimeout(() => { clearInterval(poll); setCategorizing(false); }, 120000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur catégorisation");
      setCategorizing(false);
    }
  };

  const handleCategoryChange = async (d, newCat) => {
    try {
      const res = await bankStatementAPI.updateDebitCategory(d.statement_id, d._idx, newCat);
      const catName = categories.find(c => c.id === newCat)?.label || newCat;
      if (res.data.updated_count > 0) {
        toast.success(`${catName} appliqué à ${res.data.updated_count + 1} débit(s) similaires`);
      }
      loadDebits();
    } catch { toast.error("Erreur modification"); }
  };

  const handleDeleteDebit = async (d) => {
    if (!d.id || !d.statement_id) {
      toast.error("Impossible de supprimer cette ligne (identifiant manquant)");
      return;
    }
    try {
      await bankStatementAPI.deleteDebit(d.statement_id, d.id);
      toast.success("Ligne supprimée");
      loadDebits();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur suppression");
    }
  };

  const openManualDialog = () => {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, "0");
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    setManualForm({ date: `${dd}/${mm}/${today.getFullYear()}`, label: "", amount: "", category: "" });
    setManualDialogOpen(true);
  };

  const handleManualSubmit = async () => {
    const { date, label, amount, category } = manualForm;
    if (!date || !label || !amount || parseFloat(amount) <= 0) {
      toast.error("Date, libellé et montant > 0 sont obligatoires");
      return;
    }
    setManualSaving(true);
    try {
      await bankStatementAPI.addManualDebit({
        date, label,
        amount: parseFloat(String(amount).replace(",", ".")),
        category: category || undefined,
      });
      toast.success("Ligne ajoutée");
      setManualDialogOpen(false);
      loadDebits();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur ajout");
    } finally {
      setManualSaving(false);
    }
  };

  const catLabel = (id) => categories.find(c => c.id === id)?.label || id || "Non classe";

  // ── Bulk selection for "charge sans facture" marking (accrual mode) ──
  const toggleDebitSelection = (debitKey) => {
    setSelectedDebitIds(prev => {
      const next = new Set(prev);
      if (next.has(debitKey)) next.delete(debitKey); else next.add(debitKey);
      return next;
    });
  };

  const toggleSelectAllVisible = (visibleDebits) => {
    setSelectedDebitIds(prev => {
      const visibleIds = visibleDebits.map(d => d.id).filter(Boolean);
      const allSelected = visibleIds.length > 0 && visibleIds.every(id => prev.has(id));
      if (allSelected) {
        // unselect all visible
        const next = new Set(prev);
        visibleIds.forEach(id => next.delete(id));
        return next;
      }
      // select all visible
      return new Set([...prev, ...visibleIds]);
    });
  };

  const clearSelection = () => setSelectedDebitIds(new Set());

  const handleBulkSetExcluded = async (excluded) => {
    const ids = Array.from(selectedDebitIds);
    if (ids.length === 0) return;
    setBulkSaving(true);
    try {
      const res = await bankStatementAPI.bulkSetExcluded(ids, excluded);
      toast.success(
        excluded
          ? `${res.data.updated || ids.length} ligne(s) exclue(s) du Compte de résultat`
          : `${res.data.updated || ids.length} ligne(s) réintégrée(s)`
      );
      clearSelection();
      loadDebits();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur application en lot");
    } finally {
      setBulkSaving(false);
    }
  };

  const getYearOptions = () => {
    const current = new Date().getFullYear();
    const years = [];
    for (let y = current; y >= current - 5; y--) years.push(y);
    return years;
  };

  // Returns N most recent fiscal year starts (years), e.g. for startMonth=10
  // and today=2026-06: [2025, 2024, 2023, 2022, 2021, 2020] corresponding to
  // exercises 2025-2026, 2024-2025, ...
  const getFiscalYearOptions = () => {
    const baseline = computeDefaultFyStart(companyStartMonth);
    const years = [];
    for (let y = baseline; y >= baseline - 5; y--) years.push(y);
    return years;
  };

  const filteredDebits = useMemo(() => filterCat ? debits.filter(d => d.category === filterCat) : debits, [debits, filterCat]);
  const filteredTotal = useMemo(() => filterCat ? round2(filteredDebits.reduce((s, d) => s + (d.amount || 0), 0)) : total, [filteredDebits, filterCat, total]);
  const sortedCategories = useMemo(() =>
    Object.entries(byCategory).sort((a, b) => b[1].total - a[1].total),
    [byCategory]
  );
  const uncategorizedCount = useMemo(() => debits.filter(d => !d.category).length, [debits]);

  return (
    <div className="space-y-4">
      {/* Period selector (fiscal year aware) */}
      <Card className="p-4 border border-slate-200 rounded-lg bg-white" data-testid="debits-period">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          {getFiscalYearOptions().map(y => (
            <Button key={y} size="sm" variant={fyStart === y ? "default" : "outline"} className="h-7 text-xs px-3"
              onClick={() => { setFyStart(y); setFullYear(true); setSelectedMonth(null); }}
              data-testid={`debit-fy-${y}`}>
              {fyLabel(y, companyStartMonth)}
            </Button>
          ))}
          <span className="mx-1 text-slate-300">|</span>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={fullYear}
              onChange={(e) => {
                setFullYear(e.target.checked);
                if (e.target.checked) setSelectedMonth(null);
              }}
              className="rounded"
              data-testid="full-year-checkbox"
            />
            <span className="text-xs text-slate-600">Tout l&apos;exercice</span>
          </label>
          <span className="ml-auto text-[10px] text-slate-400 font-mono">
            {fyRangeText(fyStart, companyStartMonth)}
          </span>
        </div>
        {!fullYear && (
          <div className="flex flex-wrap gap-1">
            {fiscalMonths().map(m => (
              <Button key={m.value} size="sm" variant={selectedMonth === m.value ? "default" : "ghost"} className="h-6 text-[11px] px-2"
                onClick={() => { setSelectedMonth(m.value); setFullYear(false); }}
                data-testid={`debit-month-${m.value}`}>
                {m.label}
              </Button>
            ))}
          </div>
        )}
      </Card>

      {/* Import zone: bouton "Charger" supprimé sur demande user — l'upload
          des relevés (débits + crédits) se fait via l'onglet Crédits. On
          garde "Ajouter une ligne" manuel + un message d'orientation. */}
      <div className="flex flex-wrap gap-3">
        <Card className="p-3 border border-dashed border-slate-300 rounded-lg bg-slate-50/50 flex-1 flex items-center justify-between" data-testid="import-debits-zone">
          <div>
            <p className="text-sm font-medium text-slate-700">Importer des débits</p>
            <p className="text-xs text-slate-400">
              Utilisez l&apos;onglet <button onClick={() => setTab && setTab("import")} className="text-indigo-600 hover:underline font-medium" data-testid="goto-import-tab">Crédits</button> pour importer un relevé bancaire — vos débits seront extraits automatiquement.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={openManualDialog}
            className="text-xs h-8 ml-2 border-slate-300 hover:bg-slate-100"
            data-testid="add-manual-debit-btn">
            <Plus size={13} className="mr-1" /> Ajouter une ligne
          </Button>
        </Card>
        {debits.length > 0 && (
          <Card className="p-3 border border-slate-200 rounded-lg bg-white flex items-center gap-3">
            <div>
              <p className="text-sm font-medium text-slate-700">Catégoriser par IA</p>
              <p className="text-xs text-slate-400">{uncategorizedCount} non classé(s)</p>
            </div>
            <Button size="sm" onClick={handleCategorize} disabled={categorizing || uncategorizedCount === 0}
              className="bg-violet-600 hover:bg-violet-700 text-white text-xs h-8" data-testid="categorize-btn">
              {categorizing ? <><Loader2 size={13} className="animate-spin mr-1" /> Catégorisation...</> : "Catégoriser"}
            </Button>
          </Card>
        )}
      </div>

      {/* Totals: global + by category */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className={`p-3 border rounded-lg cursor-pointer transition-all ${!filterCat ? "border-red-300 bg-red-50/60 ring-1 ring-red-200" : "border-slate-200 bg-white hover:border-red-200"}`}
          onClick={() => setFilterCat("")} data-testid="debits-total">
          <p className="text-[10px] text-red-600 uppercase font-medium">Total débits</p>
          <p className="text-lg font-bold text-red-700 money-value">{formatEur(total)}</p>
          <p className="text-[10px] text-slate-400">{debits.length} op.</p>
        </Card>
        {sortedCategories.map(([catId, data]) => (
          <Card key={catId}
            className={`p-3 border rounded-lg cursor-pointer transition-all ${filterCat === catId ? "border-indigo-300 bg-indigo-50/60 ring-1 ring-indigo-200" : "border-slate-200 bg-white hover:border-slate-300"}`}
            onClick={() => setFilterCat(filterCat === catId ? "" : catId)}
            data-testid={`cat-card-${catId}`}
          >
            <p className="text-[10px] text-slate-500 uppercase font-medium truncate">{catLabel(catId)}</p>
            <p className="text-base font-bold text-slate-900 money-value">{formatEur(data.total)}</p>
            <p className="text-[10px] text-slate-400">{data.count} op.</p>
          </Card>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin h-5 w-5 text-slate-400" /></div>
      ) : filteredDebits.length === 0 ? (
        <Card className="p-8 text-center">
          <Banknote size={32} className="mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">{filterCat ? "Aucun débit dans cette catégorie" : "Aucun débit trouvé pour cette période"}</p>
          <p className="text-xs text-slate-400 mt-1">{filterCat ? <button onClick={() => setFilterCat("")} className="text-indigo-600 hover:underline">Voir tous</button> : "Importez un relevé bancaire"}</p>
        </Card>
      ) : (
        <Card className="border border-slate-200 rounded-lg bg-white overflow-hidden">
          {filterCat && (
            <div className="px-4 py-2 bg-indigo-50 border-b border-indigo-100 flex items-center justify-between">
              <p className="text-xs text-indigo-700 font-medium">Filtre: {catLabel(filterCat)} — {filteredDebits.length} op. — {formatEur(filteredTotal)}</p>
              <button onClick={() => setFilterCat("")} className="text-xs text-indigo-600 hover:underline">Tout afficher</button>
            </div>
          )}
          {/* Bulk action bar (visible when ≥1 row selected) */}
          {selectedDebitIds.size > 0 && (
            <div className="px-4 py-2 bg-red-50 border-b border-red-200 flex items-center justify-between gap-3 flex-wrap" data-testid="bulk-action-bar">
              <p className="text-xs text-red-800 font-medium">
                {selectedDebitIds.size} ligne(s) sélectionnée(s)
              </p>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => handleBulkSetExcluded(true)} disabled={bulkSaving}
                  className="h-7 text-[11px] bg-red-600 hover:bg-red-700 text-white" data-testid="bulk-exclude-btn"
                  title="Exclure ces lignes du Compte de résultat (virements internes, remboursements de prêt, apports CCA, remboursements TVA…)">
                  {bulkSaving ? <Loader2 size={11} className="animate-spin mr-1" /> : null}
                  Exclure du Compte de résultat
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleBulkSetExcluded(false)} disabled={bulkSaving}
                  className="h-7 text-[11px] border-red-300 text-red-800 hover:bg-red-100" data-testid="bulk-reinclude-btn">
                  Réintégrer
                </Button>
                <button onClick={clearSelection} className="text-[11px] text-slate-500 hover:text-slate-700 underline px-1" data-testid="bulk-clear-selection">
                  Annuler
                </button>
              </div>
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-[10px] uppercase text-slate-500 px-2 w-8">
                  <input
                    type="checkbox"
                    checked={filteredDebits.length > 0 && filteredDebits.every(d => d.id && selectedDebitIds.has(d.id))}
                    onChange={() => toggleSelectAllVisible(filteredDebits)}
                    className="rounded cursor-pointer"
                    data-testid="debit-select-all"
                  />
                </TableHead>
                <TableHead className="text-[10px] uppercase text-slate-500 px-3">Date</TableHead>
                <TableHead className="text-[10px] uppercase text-slate-500 px-3">Libellé</TableHead>
                <TableHead className="text-[10px] uppercase text-slate-500 px-3 w-44">Catégorie</TableHead>
                <TableHead className="text-[10px] uppercase text-slate-500 px-3 text-right">Montant</TableHead>
                <TableHead className="text-[10px] uppercase text-slate-500 px-2 w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDebits.map((d, i) => {
                const isSelected = d.id && selectedDebitIds.has(d.id);
                const isExcluded = d.excluded_from_compte_resultat === true || d.is_charge_without_invoice === true; // backwards-compat read
                return (
                <TableRow key={`${d.statement_id}-${d.id || d._idx}-${i}`}
                  className={`hover:bg-slate-50/50 ${isSelected ? "bg-amber-50/40" : ""}`}>
                  <TableCell className="px-2">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => d.id && toggleDebitSelection(d.id)}
                      disabled={!d.id}
                      className="rounded cursor-pointer"
                      data-testid={`debit-checkbox-${i}`}
                    />
                  </TableCell>
                  <TableCell className="text-[11px] px-3 whitespace-nowrap">
                    {d.date}
                    {d.manual && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 font-medium" title="Ligne ajoutée manuellement">M</span>}
                  </TableCell>
                  <TableCell className="text-[11px] px-3 text-slate-700">
                    {d.label}
                    {isExcluded && (
                      <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 font-medium border border-red-200"
                        title="Cette ligne est EXCLUE du Compte de résultat — utilisé pour : virements internes, remboursements de prêt, apports en compte courant associé, remboursements TVA reçus, et doublons confirmés de factures fournisseurs.">
                        Exclus du résultat
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="px-3">
                    <select value={d.category || ""} onChange={(e) => handleCategoryChange(d, e.target.value)}
                      className="text-[11px] border border-slate-200 rounded px-1.5 py-0.5 bg-white w-full"
                      data-testid={`cat-select-${i}`}>
                      <option value="">Non classé</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                  </TableCell>
                  <TableCell className="text-[11px] px-3 text-right font-medium text-red-600">{formatEur(d.amount)}</TableCell>
                  <TableCell className="px-2">
                    <div className="flex items-center justify-end gap-1">
                      {d.supplier_invoice_id ? (
                        <button
                          onClick={() => setSupplierLinkDebit({ ...d, _unlink: true })}
                          className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 transition-colors"
                          title={`Liée à ${d.supplier_invoice_number || d.supplier_invoice_id}. Cliquez pour délier.`}
                          data-testid={`debit-linked-${i}`}>
                          <Link2 size={10} /> {d.supplier_invoice_number || "Liée"}
                        </button>
                      ) : (
                        <button
                          onClick={() => setSupplierLinkDebit(d)}
                          className="text-slate-300 hover:text-indigo-600 transition-colors p-0.5"
                          title="Lier à une facture fournisseur"
                          data-testid={`link-supplier-${i}`}>
                          <Link2 size={12} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteDebit(d)}
                        className="text-slate-300 hover:text-red-500 transition-colors p-0.5"
                        title="Supprimer cette ligne"
                        data-testid={`delete-debit-${i}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
      <Dialog open={manualDialogOpen} onOpenChange={setManualDialogOpen}>
        <DialogContent className="max-w-md" data-testid="manual-debit-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Ajouter une ligne manuellement</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label className="text-xs">Date (JJ/MM/AAAA)</Label>
              <Input value={manualForm.date}
                onChange={(e) => setManualForm({ ...manualForm, date: e.target.value })}
                placeholder="02/10/2024" className="h-8 text-xs"
                data-testid="manual-debit-date" />
            </div>
            <div>
              <Label className="text-xs">Libellé</Label>
              <Input value={manualForm.label}
                onChange={(e) => setManualForm({ ...manualForm, label: e.target.value })}
                placeholder="INT DEBITEURS ET CION DECOUVERT..." className="h-8 text-xs"
                data-testid="manual-debit-label" />
            </div>
            <div>
              <Label className="text-xs">Montant (€)</Label>
              <Input type="text" inputMode="decimal" value={manualForm.amount}
                onChange={(e) => setManualForm({ ...manualForm, amount: e.target.value })}
                placeholder="12.27" className="h-8 text-xs"
                data-testid="manual-debit-amount" />
            </div>
            <div>
              <Label className="text-xs">Catégorie</Label>
              <select value={manualForm.category}
                onChange={(e) => setManualForm({ ...manualForm, category: e.target.value })}
                className="text-xs border border-slate-200 rounded px-2 py-1.5 bg-white w-full h-8"
                data-testid="manual-debit-category">
                <option value="">Non classé</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setManualDialogOpen(false)}
                className="text-xs h-8" data-testid="manual-debit-cancel">Annuler</Button>
              <Button size="sm" onClick={handleManualSubmit} disabled={manualSaving}
                className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-8"
                data-testid="manual-debit-save">
                {manualSaving ? <><Loader2 size={12} className="animate-spin mr-1" /> Ajout...</> : "Ajouter"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <SupplierInvoiceLinkDialog
        debit={supplierLinkDebit}
        onClose={() => setSupplierLinkDebit(null)}
        onDone={() => { setSupplierLinkDebit(null); loadDebits(); }}
      />
    </div>
  );
}

// ====== Supplier Invoice Link Dialog ======
// Used in DebitsView to attach (or detach) a bank debit to a supplier invoice.
// Auto-suggests matches by amount + supplier-name fragment in the debit label.
function SupplierInvoiceLinkDialog({ debit, onClose, onDone }) {
  const [suggestions, setSuggestions] = useState([]);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!debit || debit._unlink) { setSuggestions([]); setSearch(""); setResults([]); return; }
    setLoading(true);
    bankStatementAPI.suggestSupplierInvoice(debit.id)
      .then(r => setSuggestions(r.data.suggestions || []))
      .catch(() => setSuggestions([]))
      .finally(() => setLoading(false));
  }, [debit]);

  const doSearch = async (q) => {
    setSearch(q);
    if (q.length < 2) { setResults([]); return; }
    try {
      const res = await supplierInvoiceAPI.list({ search: q, limit: 30 });
      setResults(res.data.items || []);
    } catch { setResults([]); }
  };

  const handleLink = async (inv) => {
    if (!debit) return;
    setSubmitting(true);
    try {
      await bankStatementAPI.linkDebitToSupplierInvoice(debit.id, inv.id);
      toast.success(`Débit lié à la facture ${inv.invoice_number || inv.id}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du rapprochement");
    } finally { setSubmitting(false); }
  };

  const handleUnlink = async () => {
    if (!debit) return;
    setSubmitting(true);
    try {
      await bankStatementAPI.unlinkDebitFromSupplierInvoice(debit.id);
      toast.success("Rapprochement supprimé");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  if (!debit) return null;
  const isUnlinkMode = !!debit._unlink;

  return (
    <Dialog open={!!debit} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="supplier-link-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm">
            {isUnlinkMode ? "Délier la facture fournisseur" : "Rapprocher ce débit à une facture fournisseur"}
          </DialogTitle>
        </DialogHeader>
        <div className="p-2 bg-slate-50 rounded text-[11px] mb-3">
          <p className="font-medium">{debit.label}</p>
          <p className="text-slate-500">{debit.date} — {formatEur(debit.amount)}</p>
        </div>

        {isUnlinkMode ? (
          <div className="space-y-3">
            <p className="text-xs text-slate-600">
              Cette ligne est actuellement liée à la facture{" "}
              <span className="font-semibold text-emerald-700">{debit.supplier_invoice_number || debit.supplier_invoice_id}</span>
              {debit.supplier_name ? <> ({debit.supplier_name})</> : null}.
            </p>
            <p className="text-[11px] text-slate-400">
              En délier permettra à ce débit de compter à nouveau dans les charges du Compte de résultat (méthode engagement).
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={onClose} className="text-xs h-8">Annuler</Button>
              <Button size="sm" onClick={handleUnlink} disabled={submitting}
                className="bg-red-600 hover:bg-red-700 text-white text-xs h-8" data-testid="unlink-confirm">
                {submitting ? "Suppression..." : "Délier"}
              </Button>
            </div>
          </div>
        ) : (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-3"><Loader2 className="animate-spin h-4 w-4 text-slate-400" /></div>
            ) : suggestions.length > 0 && (
              <>
                <p className="text-[10px] text-slate-500 uppercase mb-1.5">Suggestions auto · {suggestions.length}</p>
                <div className="space-y-0.5 mb-3">
                  {suggestions.map(s => (
                    <button key={s.id} onClick={() => handleLink(s)} disabled={submitting}
                      className="w-full flex items-center justify-between p-2 hover:bg-indigo-50 rounded border border-slate-200 text-left transition-colors"
                      data-testid={`suggestion-${s.id}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1">
                          <span className="font-mono text-[11px] font-medium text-indigo-600">{s.invoice_number || "—"}</span>
                          <span className="text-[10px] text-slate-400">· {s.date}</span>
                          <span className="text-[9px] px-1 py-0.5 rounded bg-emerald-50 text-emerald-700 font-medium">{s.score} pts</span>
                        </div>
                        <p className="text-[11px] text-slate-500 truncate">{s.supplier_name}</p>
                      </div>
                      <span className="text-[11px] font-medium whitespace-nowrap pl-2">{formatEur(s.total_ttc)}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
            <p className="text-[10px] text-slate-500 uppercase mb-1.5">Recherche manuelle</p>
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Fournisseur ou n° facture…" value={search} onChange={e => doSearch(e.target.value)}
                className="pl-8 h-8 text-xs" data-testid="supplier-search" />
            </div>
            <div className="space-y-0.5 mt-1 max-h-[280px] overflow-y-auto">
              {results.map(r => (
                <button key={r.id} onClick={() => handleLink(r)} disabled={submitting}
                  className="w-full flex items-center justify-between p-2 hover:bg-indigo-50 rounded text-left transition-colors"
                  data-testid={`search-result-${r.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1">
                      <span className="font-mono text-[11px] font-medium text-indigo-600">{r.invoice_number || "—"}</span>
                      <span className="text-[10px] text-slate-400">· {r.date}</span>
                    </div>
                    <p className="text-[11px] text-slate-500 truncate">{r.supplier_name}</p>
                  </div>
                  <span className="text-[11px] font-medium whitespace-nowrap pl-2">{formatEur(r.total_ttc)}</span>
                </button>
              ))}
              {!loading && search.length >= 2 && results.length === 0 && (
                <p className="text-[11px] text-slate-400 text-center py-3">Aucune facture fournisseur trouvée</p>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ====== Compte courant associé ======
// Tracks partner contributions (supplier invoices paid personally) vs
// withdrawals (bank debits categorized "dirigeant"). Net balance shows
// what the company owes the partner (positive) or vice-versa (negative).
function CompteCourantAssocieView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [categories, setCategories] = useState([]);

  const reload = useCallback(() => {
    setLoading(true);
    compteCourantAssocieAPI.get({ date_from: dateFrom, date_to: dateTo })
      .then(r => setData(r.data))
      .catch(() => toast.error("Erreur chargement compte associé"))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo]);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    bankStatementAPI.debitCategories().then(r => setCategories(r.data || [])).catch(() => {});
  }, []);

  const handleUnmarkApport = async (apport) => {
    try {
      await supplierInvoiceAPI.unmarkPaidPersonally(apport.invoice_id);
      toast.success(`Apport annulé : ${apport.supplier_name}`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const handleRecategorize = async (prelev, newCategory) => {
    if (!newCategory || newCategory === "dirigeant") return; // No-op if same category
    try {
      await bankStatementAPI.updateDebitCategoryById(prelev.debit_id, newCategory);
      toast.success("Débit recatégorisé — retiré du compte associé");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-slate-400" size={28} /></div>;
  if (!data) return null;

  const balance = data.balance || 0;
  const owesPartner = balance > 0.005;
  let balanceColor = "text-slate-600";
  let balanceBg = "bg-slate-50 border-slate-200";
  let balanceMessage = "Compte courant équilibré";
  if (owesPartner) {
    balanceColor = "text-emerald-700";
    balanceBg = "bg-emerald-50 border-emerald-200";
    balanceMessage = "La société vous doit cet apport";
  } else if (balance < -0.005) {
    balanceColor = "text-red-700";
    balanceBg = "bg-red-50 border-red-200";
    balanceMessage = "Vous devez cet écart à la société";
  }

  return (
    <div className="space-y-4" data-testid="cca-view">
      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CalendarRange size={14} className="text-slate-500" />
          <span className="text-xs text-slate-500 font-medium">Période :</span>
        </div>
        <DateField value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 w-[150px] text-xs" data-testid="cca-date-from" />
        <span className="text-xs text-slate-400">→</span>
        <DateField value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 w-[150px] text-xs" data-testid="cca-date-to" />
        {(dateFrom || dateTo) && (
          <Button variant="ghost" size="sm" className="text-xs h-8" onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="cca-clear-dates">
            Tout l&apos;historique
          </Button>
        )}
      </div>

      {/* Balance + summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="p-4 border-emerald-200 bg-emerald-50/50" data-testid="cca-apports-card">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={15} className="text-emerald-600" />
            <span className="text-xs font-medium text-emerald-700">Apports (vous payez)</span>
          </div>
          <p className="text-xl font-bold text-emerald-800">{formatEur(data.total_apports)}</p>
          <p className="text-[10px] text-emerald-600 mt-0.5">{data.apports.length} facture(s) réglée(s) en perso</p>
        </Card>
        <Card className="p-4 border-red-200 bg-red-50/50" data-testid="cca-prelevements-card">
          <div className="flex items-center gap-2 mb-1">
            <TrendingDown size={15} className="text-red-600" />
            <span className="text-xs font-medium text-red-700">Prélèvements (vous retirez)</span>
          </div>
          <p className="text-xl font-bold text-red-800">{formatEur(data.total_prelevements)}</p>
          <p className="text-[10px] text-red-600 mt-0.5">{data.prelevements.length} virement(s) dirigeant</p>
        </Card>
        <Card className={`p-4 border ${balanceBg}`} data-testid="cca-balance-card">
          <div className="flex items-center gap-2 mb-1">
            <Wallet size={15} className={balanceColor} />
            <span className={`text-xs font-medium ${balanceColor}`}>Solde net</span>
          </div>
          <p className={`text-xl font-bold ${balanceColor}`} data-testid="cca-balance">
            {owesPartner ? "+" : ""}{formatEur(balance)}
          </p>
          <p className={`text-[10px] mt-0.5 ${balanceColor}`}>{balanceMessage}</p>
        </Card>
      </div>

      {/* Apports + prélèvements en parallèle */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={14} className="text-emerald-600" />
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Apports — Factures payées en perso</h3>
          </div>
          {data.apports.length === 0 ? (
            <p className="text-[11px] text-slate-400 text-center py-6">Aucun apport sur la période</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-[10px] uppercase text-slate-500 px-2">Date</TableHead>
                  <TableHead className="text-[10px] uppercase text-slate-500 px-2">Fournisseur</TableHead>
                  <TableHead className="text-[10px] uppercase text-slate-500 px-2 text-right">Montant</TableHead>
                  <TableHead className="text-[10px] uppercase text-slate-500 px-2 w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.apports.map(a => (
                  <TableRow key={a.invoice_id} data-testid={`cca-apport-${a.invoice_id}`}>
                    <TableCell className="text-[11px] px-2 whitespace-nowrap">{a.date}</TableCell>
                    <TableCell className="text-[11px] px-2">
                      <div>{a.supplier_name || "—"}</div>
                      {a.invoice_number && <div className="font-mono text-[10px] text-slate-400">{a.invoice_number}</div>}
                      {a.note && <div className="text-[10px] text-slate-400 italic">{'\u00ab'}&nbsp;{a.note}&nbsp;{'\u00bb'}</div>}
                    </TableCell>
                    <TableCell className="text-[11px] px-2 text-right font-medium text-emerald-700">{formatEur(a.amount)}</TableCell>
                    <TableCell className="px-1">
                      <button onClick={() => handleUnmarkApport(a)}
                        className="text-slate-300 hover:text-amber-500 transition-colors p-0.5"
                        title="Annuler le paiement perso (retire du compte associé)"
                        data-testid={`cca-unmark-apport-${a.invoice_id}`}>
                        <X size={12} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <TrendingDown size={14} className="text-red-600" />
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Prélèvements — Débits {'\u00ab'}&nbsp;dirigeant&nbsp;{'\u00bb'}</h3>
          </div>
          {data.prelevements.length === 0 ? (
            <p className="text-[11px] text-slate-400 text-center py-6">Aucun prélèvement sur la période</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-[10px] uppercase text-slate-500 px-2">Date</TableHead>
                  <TableHead className="text-[10px] uppercase text-slate-500 px-2">Libellé</TableHead>
                  <TableHead className="text-[10px] uppercase text-slate-500 px-2 text-right">Montant</TableHead>
                  <TableHead className="text-[10px] uppercase text-slate-500 px-2 w-32">Catégorie</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.prelevements.map(p => (
                  <TableRow key={p.debit_id} data-testid={`cca-prelevement-${p.debit_id}`}>
                    <TableCell className="text-[11px] px-2 whitespace-nowrap">{p.date}</TableCell>
                    <TableCell className="text-[11px] px-2 truncate max-w-[260px]">{p.label}</TableCell>
                    <TableCell className="text-[11px] px-2 text-right font-medium text-red-700">{formatEur(p.amount)}</TableCell>
                    <TableCell className="px-1">
                      <select
                        value="dirigeant"
                        onChange={e => handleRecategorize(p, e.target.value)}
                        className="w-full text-[10px] border border-slate-200 rounded px-1 py-0.5 bg-white hover:border-amber-300 focus:border-amber-400 outline-none cursor-pointer"
                        data-testid={`cca-recat-${p.debit_id}`}>
                        <option value="dirigeant">Garder ici (dirigeant)</option>
                        {categories.filter(c => c.id !== "dirigeant").map(c => (
                          <option key={c.id} value={c.id}>→ {c.label}</option>
                        ))}
                      </select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>

      <p className="text-[10px] text-slate-400 italic">
        💡 Les apports proviennent des factures fournisseurs marquées &laquo;&nbsp;Payée en perso&nbsp;&raquo;. Les prélèvements correspondent aux débits bancaires catégorisés &laquo;&nbsp;Prélèvements dirigeant&nbsp;&raquo;.
      </p>
    </div>
  );
}

// ====== Compte de Résultat helpers ======
// Fiscal year period defined by `fiscal_year_start_month` in company
// settings. Defaults to month 1 (calendar year, 1 Jan → 31 Dec).
// Auto-detect: if today's month is >= start month → current FY starts
// this year; otherwise it started last year.
function getCurrentFiscalYearStart(startMonth) {
  const m0 = (startMonth || 1) - 1; // 0-indexed
  const now = new Date();
  const cm = now.getMonth();
  const cy = now.getFullYear();
  return cm >= m0 ? cy : cy - 1;
}

const MONTH_NAMES_FR = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

function fiscalYearLabel(startYear, startMonth) {
  if (!startMonth || startMonth === 1) return `${startYear}`;
  return `${startYear} - ${startYear + 1}`;
}

function fiscalYearRange(startYear, startMonth) {
  const m = startMonth || 1;
  const fromMonth = String(m).padStart(2, "0");
  if (m === 1) {
    return { from: `${startYear}-01-01`, to: `${startYear}-12-31` };
  }
  const endMonth = m - 1;
  const endMonthStr = String(endMonth).padStart(2, "0");
  // Last day of end month
  const lastDay = new Date(startYear + 1, endMonth, 0).getDate();
  return {
    from: `${startYear}-${fromMonth}-01`,
    to: `${startYear + 1}-${endMonthStr}-${String(lastDay).padStart(2, "0")}`,
  };
}

function fiscalYearRangeText(startYear, startMonth) {
  const m = startMonth || 1;
  if (m === 1) return `du 1ᵉʳ janvier au 31 décembre ${startYear}`;
  return `du 1ᵉʳ ${MONTH_NAMES_FR[m - 1]} ${startYear} au ${new Date(startYear + 1, m - 1, 0).getDate()} ${MONTH_NAMES_FR[m - 2]} ${startYear + 1}`;
}

// ====== Compte de Résultat ======
function CompteRésultatView() {
  const [settings, setSettings] = useState({ fiscal_year_start_month: 1, accounting_method: "cash" });
  const startMonth = settings.fiscal_year_start_month || 1;
  const defaultStart = getCurrentFiscalYearStart(startMonth);
  const [fyStart, setFyStart] = useState(defaultStart);
  const [accountingMethod, setAccountingMethod] = useState("cash");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  // Duplicates detection (Engagement mode only)
  const [dupCandidates, setDupCandidates] = useState([]);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupLoading, setDupLoading] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  // Load company fiscal year settings once
  useEffect(() => {
    adminAPI.getSettings().then(r => {
      const s = r.data || {};
      const sm = s.fiscal_year_start_month || 1;
      setSettings(s);
      setFyStart(getCurrentFiscalYearStart(sm));
      setAccountingMethod(s.accounting_method || "cash");
      setSettingsLoaded(true);
    }).catch(() => setSettingsLoaded(true));
  }, []);

  const loadData = useCallback(async () => {
    if (!settingsLoaded) return;
    setLoading(true);
    try {
      const { from, to } = fiscalYearRange(fyStart, startMonth);
      const res = await bankStatementAPI.compteRésultat({ date_from: from, date_to: to, method: accountingMethod });
      setData(res.data);
    } catch { toast.error("Erreur chargement compte de résultat"); }
    finally { setLoading(false); }
  }, [fyStart, startMonth, accountingMethod, settingsLoaded]);

  useEffect(() => { loadData(); }, [loadData]);

  // Detect duplicates only in Engagement (Accrual) mode — they don't apply
  // to cash accounting (no supplier invoices are counted in cash mode).
  const loadDuplicates = useCallback(async () => {
    if (!settingsLoaded || accountingMethod !== "accrual") {
      setDupCandidates([]);
      return;
    }
    setDupLoading(true);
    try {
      const { from, to } = fiscalYearRange(fyStart, startMonth);
      const res = await bankStatementAPI.detectDuplicates({ date_from: from, date_to: to });
      setDupCandidates(res.data?.candidates || []);
    } catch { setDupCandidates([]); }
    finally { setDupLoading(false); }
  }, [fyStart, startMonth, accountingMethod, settingsLoaded]);

  useEffect(() => { loadDuplicates(); }, [loadDuplicates]);

  const handleConfirmDuplicate = async (candidate) => {
    try {
      await bankStatementAPI.confirmDuplicate(candidate.debit.id, candidate.invoice.id);
      toast.success(`Doublon confirmé : débit lié à ${candidate.invoice.number || ""}`);
      setDupCandidates(prev => prev.filter(c => !(c.debit.id === candidate.debit.id && c.invoice.id === candidate.invoice.id)));
      loadData(); // refresh P&L (excluded debit → resultat changes)
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const handleDismissDuplicate = async (candidate) => {
    try {
      await bankStatementAPI.dismissDuplicate(candidate.debit.id, candidate.invoice.id);
      setDupCandidates(prev => prev.filter(c => !(c.debit.id === candidate.debit.id && c.invoice.id === candidate.invoice.id)));
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const { from, to } = fiscalYearRange(fyStart, startMonth);
      const res = await bankStatementAPI.exportCompteResultatPdf({ date_from: from, date_to: to, method: accountingMethod });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const label = startMonth === 1 ? `${fyStart}` : `${fyStart}-${fyStart + 1}`;
      a.download = `Compte_de_resultat_${label}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur génération PDF");
    } finally { setExportingPdf(false); }
  };

  // Build 6 fiscal years: current + 5 previous
  const fiscalYears = [];
  for (let s = defaultStart; s >= defaultStart - 5; s--) fiscalYears.push(s);

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-slate-400" size={28} /></div>;
  if (!data) return null;

  const isPositive = data.resultat >= 0;
  const { from, to } = fiscalYearRange(fyStart, startMonth);
  const fyLabelPrefix = (startMonth === 1) ? "Année" : "Exercice";

  return (
    <div className="space-y-4" data-testid="compte-resultat-view">
      {/* Fiscal year + accounting method selector */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CalendarRange size={14} className="text-slate-500" />
          <span className="text-xs text-slate-500 font-medium">{fyLabelPrefix} :</span>
        </div>
        <Select value={String(fyStart)} onValueChange={(v) => setFyStart(Number(v))}>
          <SelectTrigger className="h-8 w-[220px] text-xs" data-testid="cr-fy-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {fiscalYears.map(s => (
              <SelectItem key={s} value={String(s)} className="text-xs" data-testid={`cr-fy-${s}`}>
                {fyLabelPrefix} {fiscalYearLabel(s, startMonth)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-[11px] text-slate-400" data-testid="cr-fy-range">
          {fiscalYearRangeText(fyStart, startMonth)}
        </span>
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs text-slate-500 font-medium">Méthode :</span>
          <Select value={accountingMethod} onValueChange={setAccountingMethod}>
            <SelectTrigger className="h-8 w-[180px] text-xs" data-testid="cr-method-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cash" className="text-xs">Trésorerie</SelectItem>
              <SelectItem value="accrual" className="text-xs">Engagement</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={handleExportPdf} disabled={exportingPdf}
            className="h-8 text-xs ml-2" data-testid="export-cr-pdf-btn">
            {exportingPdf ? <Loader2 size={13} className="animate-spin mr-1" /> : <FileText size={13} className="mr-1" />}
            Exporter en PDF
          </Button>
        </div>
      </div>

      {/* Duplicates alert panel (Engagement mode only) */}
      {accountingMethod === "accrual" && dupCandidates.length > 0 && (
        <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-amber-300 bg-amber-50" data-testid="duplicates-alert">
          <div className="flex items-center gap-2 text-amber-900">
            <AlertCircle size={16} />
            <p className="text-xs font-medium">
              <span className="font-bold">{dupCandidates.length}</span> doublon{dupCandidates.length > 1 ? "s" : ""} probable{dupCandidates.length > 1 ? "s" : ""} détecté{dupCandidates.length > 1 ? "s" : ""}
              <span className="text-amber-700 ml-1">(facture fournisseur + débit bancaire correspondant)</span>
            </p>
          </div>
          <Button size="sm" onClick={() => setDupOpen(true)} className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white" data-testid="open-duplicates-dialog">
            Examiner
          </Button>
        </div>
      )}

      {/* Hidden range markers for tests */}
      <input type="hidden" data-testid="cr-date-from" value={from} readOnly />
      <input type="hidden" data-testid="cr-date-to" value={to} readOnly />

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4 border-emerald-200 bg-emerald-50/50">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={15} className="text-emerald-600" />
            <span className="text-xs font-medium text-emerald-700">
              {accountingMethod === "accrual" ? "Produits (CA HT émis)" : "Produits (CA HT encaissé)"}
            </span>
          </div>
          <p className="text-xl font-bold text-emerald-800" data-testid="cr-produits">{formatEur(data.produits_ht)}</p>
          <p className="text-[10px] text-emerald-600 mt-0.5">
            {data.factures_payees} facture(s) {accountingMethod === "accrual" ? "émise(s)" : "payée(s)"}
          </p>
        </Card>
        <Card className="p-4 border-red-200 bg-red-50/50">
          <div className="flex items-center gap-2 mb-1">
            <TrendingDown size={15} className="text-red-600" />
            <span className="text-xs font-medium text-red-700">Charges (débits bancaires)</span>
          </div>
          <p className="text-xl font-bold text-red-800" data-testid="cr-charges">{formatEur(data.total_charges)}</p>
          <p className="text-[10px] text-red-600 mt-0.5">{data.total_debits} débit(s)</p>
        </Card>
        <Card className={`p-4 ${isPositive ? "border-blue-200 bg-blue-50/50" : "border-orange-200 bg-orange-50/50"}`}>
          <div className="flex items-center gap-2 mb-1">
            <BarChart3 size={15} className={isPositive ? "text-blue-600" : "text-orange-600"} />
            <span className={`text-xs font-medium ${isPositive ? "text-blue-700" : "text-orange-700"}`}>Résultat</span>
          </div>
          <p className={`text-xl font-bold ${isPositive ? "text-blue-800" : "text-orange-800"}`} data-testid="cr-résultat">{formatEur(data.resultat)}</p>
          <p className={`text-[10px] mt-0.5 ${isPositive ? "text-blue-600" : "text-orange-600"}`}>
            {isPositive ? "Bénéfice" : "Déficit"}
          </p>
        </Card>
      </div>

      {/* Detail table */}
      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs font-semibold text-slate-600">Catégorie</TableHead>
              <TableHead className="text-xs font-semibold text-slate-600 text-center">Nb</TableHead>
              <TableHead className="text-xs font-semibold text-slate-600 text-right">Montant</TableHead>
              <TableHead className="text-xs font-semibold text-slate-600 text-right">% charges</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* Produits row */}
            <TableRow className="bg-emerald-50/40 font-semibold">
              <TableCell className="text-xs text-emerald-800">
                {accountingMethod === "accrual" ? "Produits (CA HT émis)" : "Produits (CA HT encaissé)"}
              </TableCell>
              <TableCell className="text-xs text-center text-emerald-700">{data.factures_payees}</TableCell>
              <TableCell className="text-xs text-right text-emerald-800">{formatEur(data.produits_ht)}</TableCell>
              <TableCell className="text-xs text-right text-emerald-600">-</TableCell>
            </TableRow>
            {/* Separator */}
            <TableRow><TableCell colSpan={4} className="py-0.5 bg-slate-100 text-[10px] font-bold text-slate-500 px-3">CHARGES</TableCell></TableRow>
            {/* Charge lines */}
            {data.charges.map(c => (
              <TableRow key={c.id} className="hover:bg-slate-50">
                <TableCell className="text-xs text-slate-700">{c.label}</TableCell>
                <TableCell className="text-xs text-center text-slate-500">{c.count}</TableCell>
                <TableCell className="text-xs text-right text-red-700 font-medium">-{formatEur(c.total)}</TableCell>
                <TableCell className="text-xs text-right text-slate-500">
                  {data.total_charges > 0 ? `${Math.round((c.total / data.total_charges) * 100)}%` : "-"}
                </TableCell>
              </TableRow>
            ))}
            {data.charges.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-xs text-slate-400 text-center py-4">Aucun debit pour cette période</TableCell></TableRow>
            )}
            {/* Total charges */}
            <TableRow className="bg-red-50/40 font-semibold border-t-2 border-slate-200">
              <TableCell className="text-xs text-red-800">Total charges</TableCell>
              <TableCell className="text-xs text-center text-red-700">{data.total_debits}</TableCell>
              <TableCell className="text-xs text-right text-red-800">-{formatEur(data.total_charges)}</TableCell>
              <TableCell className="text-xs text-right text-red-600">100%</TableCell>
            </TableRow>
            {/* Résultat */}
            <TableRow className={`font-bold border-t-2 ${isPositive ? "bg-blue-50/40" : "bg-orange-50/40"}`}>
              <TableCell className={`text-sm ${isPositive ? "text-blue-900" : "text-orange-900"}`}>
                RÉSULTAT
              </TableCell>
              <TableCell></TableCell>
              <TableCell className={`text-sm text-right ${isPositive ? "text-blue-900" : "text-orange-900"}`} data-testid="cr-résultat-table">
                {formatEur(data.resultat)}
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Card>

      {/* Duplicates review modal */}
      {dupOpen && (
        <DuplicatesReviewDialog
          candidates={dupCandidates}
          loading={dupLoading}
          onClose={() => setDupOpen(false)}
          onConfirm={handleConfirmDuplicate}
          onDismiss={handleDismissDuplicate}
        />
      )}
    </div>
  );
}


function DuplicatesReviewDialog({ candidates, loading, onClose, onConfirm, onDismiss }) {
  const [idx, setIdx] = useState(0);
  const current = candidates[idx];
  if (!current) {
    return (
      <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="duplicates-dialog-empty">
        <Card className="max-w-md w-full p-6 text-center">
          <CheckCheck className="mx-auto text-emerald-500 mb-3" size={32} />
          <h3 className="font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Tous les doublons traités</h3>
          <p className="text-xs text-slate-600 mb-4">Aucun autre doublon probable n&apos;a été détecté pour cette période.</p>
          <Button onClick={onClose} className="text-xs" data-testid="duplicates-close">Fermer</Button>
        </Card>
      </div>
    );
  }
  const handle = async (action) => {
    if (action === "confirm") await onConfirm(current);
    else await onDismiss(current);
    // Stay on same idx — candidates list shifts (we removed this one) so the
    // next one slides into place. If we were on the last → close.
    if (candidates.length <= 1) onClose();
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="duplicates-dialog">
      <Card className="max-w-2xl w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Doublon probable
            <span className="ml-2 text-xs font-normal text-slate-500">(score {current.score}/{current.score_max})</span>
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="duplicates-close-x">
            <X size={18} />
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mb-4">{idx + 1} sur {candidates.length} à traiter</p>
        <div className="grid grid-cols-2 gap-4 mb-5">
          <div className="p-3 rounded-lg border border-slate-200 bg-slate-50/50">
            <div className="flex items-center gap-2 mb-2 text-xs font-medium text-slate-600">
              <FileText size={13} className="text-indigo-500" /> FACTURE
            </div>
            <p className="text-sm font-semibold text-slate-900" data-testid="dup-invoice-supplier">{current.invoice.supplier_name || "—"}</p>
            <p className="text-xs text-slate-600 mt-1">{current.invoice.number || "—"}</p>
            <p className="text-xs text-slate-500 mt-1">{current.invoice.date || "—"}</p>
            <p className="text-base font-bold text-slate-900 mt-2">{formatEur(current.invoice.total_ttc)}</p>
            {current.invoice.category && <p className="text-[10px] text-slate-400 mt-1">Cat: {current.invoice.category}</p>}
          </div>
          <div className="p-3 rounded-lg border border-slate-200 bg-slate-50/50">
            <div className="flex items-center gap-2 mb-2 text-xs font-medium text-slate-600">
              <Banknote size={13} className="text-red-500" /> DÉBIT BANCAIRE
            </div>
            <p className="text-sm font-semibold text-slate-900 leading-tight" data-testid="dup-debit-label">{current.debit.label || "—"}</p>
            <p className="text-xs text-slate-500 mt-1">{current.debit.date || "—"}</p>
            <p className="text-base font-bold text-red-600 mt-2">{formatEur(current.debit.amount)}</p>
            {current.debit.category && <p className="text-[10px] text-slate-400 mt-1">Cat: {current.debit.category}</p>}
          </div>
        </div>
        <div className="flex items-center justify-between gap-2">
          <Button variant="outline" onClick={() => handle("dismiss")} disabled={loading}
            className="flex-1 text-xs" data-testid="dup-dismiss-btn">
            <X size={13} className="mr-1" />Non, garder les deux
          </Button>
          {idx < candidates.length - 1 && (
            <Button variant="ghost" onClick={() => setIdx(idx + 1)} className="text-xs" data-testid="dup-skip-btn">
              Passer →
            </Button>
          )}
          <Button onClick={() => handle("confirm")} disabled={loading}
            className="flex-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dup-confirm-btn">
            <Check size={13} className="mr-1" />Oui, doublon
          </Button>
        </div>
      </Card>
    </div>
  );
}

function round2(n) { return Math.round(n * 100) / 100; }

// ====== Main Page ======
export default function BankStatementPage() {
  const [step, setStep] = useState("upload");
  const [transactions, setTransactions] = useState([]);
  const [parsedDebits, setParsedDebits] = useState([]);
  const [filename, setFilename] = useState("");
  const [fileHash, setFileHash] = useState("");
  const [storagePath, setStoragePath] = useState("");
  const [doneResult, setDoneResult] = useState(null);
  const [tab, setTab] = useState("import"); // "import" | "history" | "debits" | "resultat" | "associe"
  const [initialDraft, setInitialDraft] = useState(null);
  const [source, setSource] = useState("banque");

  const handleParsed = (txs, fname, debits, src, hash, storage) => {
    setInitialDraft(null);
    setTransactions(txs);
    setParsedDebits(debits || []);
    setFilename(fname || "");
    setFileHash(hash || "");
    setStoragePath(storage || "");
    setSource(src || "banque");
    setStep("match");
  };
  const handleDone = (result) => { setDoneResult(result); setStep("done"); };
  const reset = () => { setStep("upload"); setTransactions([]); setParsedDebits([]); setFilename(""); setFileHash(""); setStoragePath(""); setDoneResult(null); setInitialDraft(null); setSource("banque"); };

  const resumeDraft = async (draftId) => {
    try {
      const res = await bankStatementAPI.getDraft(draftId);
      const d = res.data;
      setTransactions(d.transactions || []);
      setParsedDebits(d.debits || []);
      setFilename(d.filename || "");
      setFileHash(d.file_hash || "");
      setStoragePath(d.storage_path || "");
      setSource(d.source || "banque");
      setInitialDraft({ id: d.id, results: d.results || [], assignments: d.assignments || {} });
      setTab("import");
      setStep("match");
      toast.success("Brouillon repris");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Brouillon introuvable");
    }
  };

  return (
    <div className="page-container" data-testid="bank-statement-page">
      <div className="mb-4">
        <h1 className="text-2xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Rapprochement bancaire</h1>
        <p className="text-xs text-slate-500 mt-1">
          {(() => {
            if (tab === "history") return "Historique des relevés traités";
            if (tab === "debits") return "Détail des débits bancaires";
            if (tab === "resultat") return "Synthèse produits / charges";
            if (step === "upload") return "Importez un relevé pour rapprocher les encaissements avec vos factures";
            if (step === "match") return "Vérifiez les correspondances et validez les règlements";
            return "Import terminé";
          })()}
        </p>
      </div>

      {/* Tabs: Import / History / Debits */}
      <div className="flex flex-wrap items-center gap-1 mb-4 border-b border-slate-200 pb-2">
        <button
          onClick={() => { setTab("import"); }}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-t text-xs font-medium transition-colors ${tab === "import" ? "bg-indigo-100 text-indigo-700 border-b-2 border-indigo-600" : "text-slate-500 hover:text-slate-700"}`}
          data-testid="tab-import"
        >
          <Upload size={13} /> Crédits
        </button>
        <button
          onClick={() => setTab("history")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-t text-xs font-medium transition-colors ${tab === "history" ? "bg-indigo-100 text-indigo-700 border-b-2 border-indigo-600" : "text-slate-500 hover:text-slate-700"}`}
          data-testid="tab-history"
        >
          <History size={13} /> Historique
        </button>
        <button
          onClick={() => setTab("debits")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-t text-xs font-medium transition-colors ${tab === "debits" ? "bg-red-100 text-red-700 border-b-2 border-red-600" : "text-slate-500 hover:text-slate-700"}`}
          data-testid="tab-debits"
        >
          <Banknote size={13} /> Débits
        </button>
        <button
          onClick={() => setTab("resultat")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-t text-xs font-medium transition-colors ${tab === "resultat" ? "bg-blue-100 text-blue-700 border-b-2 border-blue-600" : "text-slate-500 hover:text-slate-700"}`}
          data-testid="tab-resultat"
        >
          <BarChart3 size={13} /> Compte de résultat
        </button>
        <button
          onClick={() => setTab("associe")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-t text-xs font-medium transition-colors ${tab === "associe" ? "bg-violet-100 text-violet-700 border-b-2 border-violet-600" : "text-slate-500 hover:text-slate-700"}`}
          data-testid="tab-associe"
        >
          <Wallet size={13} /> Compte associé
        </button>
      </div>

      {tab === "resultat" && <CompteRésultatView />}
      {tab === "associe" && <CompteCourantAssocieView />}
      {tab === "debits" && <DebitsView setTab={setTab} />}
      {tab === "history" && <HistoryView onResumeDraft={resumeDraft} />}
      {tab === "import" && (
        <>
          {/* Stepper */}
          <div className="flex items-center gap-2 mb-4">
            {[
              { key: "upload", label: "1. Import", icon: Upload },
              { key: "match", label: "2. Rapprochement", icon: Link2 },
              { key: "done", label: "3. Validation", icon: Check },
            ].map((s, i) => (
              <div key={s.key} className="flex items-center gap-1.5">
                {i > 0 && <ArrowRight size={12} className="text-slate-300" />}
                <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium ${
                  step === s.key ? "bg-indigo-100 text-indigo-700" :
                  ["upload", "match", "done"].indexOf(step) > ["upload", "match", "done"].indexOf(s.key) ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"
                }`}>
                  <s.icon size={11} />{s.label}
                </div>
              </div>
            ))}
          </div>

          {step === "upload" && <UploadStep onParsed={handleParsed} />}
          {step === "match" && <MatchStep transactions={transactions} filename={filename} fileHash={fileHash} storagePath={storagePath} debits={parsedDebits} source={source} onDone={handleDone} onCancel={reset} initialDraft={initialDraft} />}
          {step === "done" && <DoneStep result={doneResult} onReset={reset} />}
        </>
      )}
    </div>
  );
}
